import { CustomerFragment } from '@/integrations/crystallize/__gql-codegen-types/pim/graphql';
import { parseCustomerMetaNukiClub } from '@/integrations/crystallize/catalogue-queries/utils/parseCustomerMetaNukiClub';
import { CRYSTALLIZE_CUSTOMER_META_NUKI_CLUB_KEY } from '@/integrations/crystallize/types/constants';
import { NukiClubMeta } from '@/integrations/crystallize/types/NukiClubMeta';
import { differenceInCalendarDays, parseISO } from 'date-fns';

/**
 * Length (in days) of the post-signup Nuki Club welcome period. While the
 * user is within this window the "Welcome / Onboarding" banners are shown;
 * after the window expires they are replaced by "Refer-a-Friend" content.
 *
 * Boundary is inclusive: day 90 still counts as "within", day 91 does not.
 */
const WELCOME_PERIOD_DAYS = 90;

export default class CrystallizeCustomerModel {
  private readonly rawCrystallizeCustomer: CustomerFragment;

  constructor(rawCrystallizeCustomer: CustomerFragment) {
    this.rawCrystallizeCustomer = rawCrystallizeCustomer;
  }

  get identifier() {
    return this.rawCrystallizeCustomer.identifier;
  }

  get firstName() {
    return this.rawCrystallizeCustomer.firstName;
  }

  get middleName() {
    return this.rawCrystallizeCustomer.middleName;
  }

  get lastName() {
    return this.rawCrystallizeCustomer.lastName;
  }

  get birthDate() {
    return this.rawCrystallizeCustomer.birthDate;
  }

  get email() {
    return this.rawCrystallizeCustomer.email;
  }

  get phone() {
    return this.rawCrystallizeCustomer.phone;
  }

  get companyName() {
    return this.rawCrystallizeCustomer.companyName;
  }

  get taxNumber() {
    return this.rawCrystallizeCustomer.taxNumber;
  }

  get addresses() {
    return this.rawCrystallizeCustomer.addresses || [];
  }

  getIdentifier() {
    return this.rawCrystallizeCustomer.identifier;
  }

  getMetadata() {
    return this.rawCrystallizeCustomer.meta || [];
  }

  getExternalReferences() {
    return this.rawCrystallizeCustomer.externalReferences || [];
  }

  getMetaField(field: string) {
    const meta = this.getMetadata();
    return meta.find((m) => m.key === field)?.value;
  }

  getExternalReference(field: string) {
    const reference = this.getExternalReferences();
    return reference.find((r) => r.key === field)?.value;
  }

  getLegacyShopId() {
    const metaWithWpId = this.getMetaField('Legacy Customer Id');
    const referenceWithWpId = this.getExternalReference('Legacy Customer Id');

    return metaWithWpId || referenceWithWpId || undefined;
  }

  /**
   * Returns the parsed Nuki Club metadata block for this customer, or
   * `undefined` if the customer has no Nuki Club meta entry. Useful for
   * reading the `lastSignUpTimestamp` (form fillout date) used to drive
   * the dynamic account banner switch.
   */
  getNukiClubMeta(): NukiClubMeta | undefined {
    return parseCustomerMetaNukiClub(this.getMetadata());
  }

  /**
   * Returns `true` while the customer is within the post-signup welcome
   * window (see {@link WELCOME_PERIOD_DAYS}). Drives the dynamic account
   * banner switch (Welcome / Onboarding vs. Refer-a-Friend).
   *
   * Source of truth: `NukiClubMeta.lastSignUpTimestamp`. Customers without a
   * timestamp (legacy members) are considered outside the window. Future
   * timestamps (clock-skew) are treated as within the window.
   */
  isWithinWelcomePeriod(now: Date = new Date()): boolean {
    const filloutDate = this.getNukiClubMeta()?.lastSignUpTimestamp;
    if (!filloutDate) {
      return false;
    }

    const parsed = parseISO(filloutDate);
    if (Number.isNaN(parsed.getTime())) {
      return false;
    }

    return differenceInCalendarDays(now, parsed) <= WELCOME_PERIOD_DAYS;
  }

  isClubMember(): boolean {
    const clubMemberMetaKey = CRYSTALLIZE_CUSTOMER_META_NUKI_CLUB_KEY;

    const clubMemberData = this.getMetaField(clubMemberMetaKey);

    if (!clubMemberData) {
      return false;
    }

    if (typeof clubMemberData === 'string') {
      try {
        const parsedData = JSON.parse(clubMemberData);
        return !!parsedData.active;
      } catch (error) {
        console.error('Error parsing club member data:', error);
        return false;
      }
    }

    return false;
  }
}
